import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { AppState } from '../app.state';
import { selectIsAuthenticated } from '../user/user.selectors';
import { isCartHydrationBlocked } from '../../utils/cart-hydration.util';

import { CartItem } from './cart.state';
import { selectCartItems } from './cart.selectors';
import { CartValidationService } from './cart-validation.service';
import { CartErrorHandlerService } from './cart-error-handler.service';

export interface PersistenceOptions {
  validateBeforeSave?: boolean;
  sanitizeBeforeSave?: boolean;
  compressData?: boolean;
  maxRetries?: number;
}

export interface PersistenceStats {
  saveCount: number;
  loadCount: number;
  errorCount: number;
  lastSave: number | null;
  lastLoad: number | null;
  lastError: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class CartPersistenceService {
  private readonly CART_STORAGE_KEY = 'umarketplace_cart_items';
  private readonly CART_BACKUP_KEY = 'umarketplace_cart_backup';
  private readonly CART_METADATA_KEY = 'umarketplace_cart_metadata';
  
  private store = inject(Store<AppState>);
  private validationService = inject(CartValidationService);
  private errorHandler = inject(CartErrorHandlerService);
  
  private stats: PersistenceStats = {
    saveCount: 0,
    loadCount: 0,
    errorCount: 0,
    lastSave: null,
    lastLoad: null,
    lastError: null
  };

  private readonly DEFAULT_OPTIONS: PersistenceOptions = {
    validateBeforeSave: true,
    sanitizeBeforeSave: true,
    compressData: false,
    maxRetries: 3
  };

  saveCartToLocalStorage(options: PersistenceOptions = {}): void {
    if (isCartHydrationBlocked()) {
      this.clearCartFromLocalStorage();
      return;
    }

    const opts = { ...this.DEFAULT_OPTIONS, ...options };
    
    combineLatest([
      this.store.select(selectCartItems),
      this.store.select(selectIsAuthenticated)
    ]).pipe(
      filter(([items, isAuthenticated]) => !isAuthenticated && items.length > 0),
      take(1)
    ).subscribe(([items]) => {
      this.saveItemsToStorage(items, opts);
    });
  }

  saveCartToLocalStorageDirect(items: CartItem[], options: PersistenceOptions = {}): void {
    if (isCartHydrationBlocked()) {
      this.clearCartFromLocalStorage();
      return;
    }

    const opts = { ...this.DEFAULT_OPTIONS, ...options };
    this.saveItemsToStorage(items, opts);
  }

  loadCartFromLocalStorage(options: PersistenceOptions = {}): CartItem[] {
    if (isCartHydrationBlocked()) {
      this.clearCartFromLocalStorage();
      return [];
    }

    const opts = { ...this.DEFAULT_OPTIONS, ...options };
    
    try {
      this.stats.loadCount++;
      this.stats.lastLoad = Date.now();

      // Try to load from main storage
      const storedCart = localStorage.getItem(this.CART_STORAGE_KEY);
      if (storedCart) {
        const items = this.parseStoredData(storedCart, opts);
        if (items && items.length > 0) {
          return items;
        }
      }

      // If main storage fails, try backup
      const backupCart = localStorage.getItem(this.CART_BACKUP_KEY);
      if (backupCart) {
        console.warn('Using backup cart data');
        const items = this.parseStoredData(backupCart, opts);
        if (items && items.length > 0) {
          return items;
        }
      }

      return [];
    } catch (error) {
      this.stats.errorCount++;
      this.stats.lastError = Date.now();
      this.errorHandler.handlePersistenceError(error, 'Load Cart from LocalStorage');
      return [];
    }
  }

  clearCartFromLocalStorage(): void {
    try {
      localStorage.removeItem(this.CART_STORAGE_KEY);
      localStorage.removeItem(this.CART_BACKUP_KEY);
      localStorage.removeItem(this.CART_METADATA_KEY);
    } catch (error) {
      this.errorHandler.handlePersistenceError(error, 'Clear Cart from LocalStorage');
    }
  }

  hasStoredCart(): boolean {
    if (isCartHydrationBlocked()) {
      this.clearCartFromLocalStorage();
      return false;
    }

    try {
      return localStorage.getItem(this.CART_STORAGE_KEY) !== null ||
             localStorage.getItem(this.CART_BACKUP_KEY) !== null;
    } catch (error) {
      this.errorHandler.handlePersistenceError(error, 'Check Stored Cart');
      return false;
    }
  }

  getStoredCartItemCount(): number {
    try {
      const items = this.loadCartFromLocalStorage();
      return items.reduce((total: number, item: CartItem) => total + item.quantity, 0);
    } catch (error) {
      this.errorHandler.handlePersistenceError(error, 'Get Stored Cart Count');
      return 0;
    }
  }

  getStoredCartMetadata(): {
    version: string;
    lastModified: number;
    itemCount: number;
    totalQuantity: number;
  } | null {
    if (isCartHydrationBlocked()) {
      this.clearCartFromLocalStorage();
      return null;
    }

    try {
      const metadata = localStorage.getItem(this.CART_METADATA_KEY);
      if (metadata) {
        return JSON.parse(metadata);
      }
    } catch (error) {
      this.errorHandler.handlePersistenceError(error, 'Get Cart Metadata');
    }
    return null;
  }

  createBackup(items: CartItem[]): void {
    if (isCartHydrationBlocked()) {
      this.clearCartFromLocalStorage();
      return;
    }

    try {
      const backupData = JSON.stringify(items);
      localStorage.setItem(this.CART_BACKUP_KEY, backupData);
    } catch (error) {
      this.errorHandler.handlePersistenceError(error, 'Create Cart Backup');
    }
  }

  restoreFromBackup(): CartItem[] {
    if (isCartHydrationBlocked()) {
      this.clearCartFromLocalStorage();
      return [];
    }

    try {
      const backupData = localStorage.getItem(this.CART_BACKUP_KEY);
      if (backupData) {
        const items = JSON.parse(backupData);
        this.saveCartToLocalStorageDirect(items);
        return items;
      }
    } catch (error) {
      this.errorHandler.handlePersistenceError(error, 'Restore from Backup');
    }
    return [];
  }

  getPersistenceStats(): PersistenceStats {
    return { ...this.stats };
  }

  resetPersistenceStats(): void {
    this.stats = {
      saveCount: 0,
      loadCount: 0,
      errorCount: 0,
      lastSave: null,
      lastLoad: null,
      lastError: null
    };
  }

  isLocalStorageAvailable(): boolean {
    try {
      const testKey = '__test_localStorage__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }

  getLocalStorageInfo(): {
    available: boolean;
    used: number;
    quota: number;
    usage: number;
  } {
    const available = this.isLocalStorageAvailable();
    
    if (!available) {
      return {
        available: false,
        used: 0,
        quota: 0,
        usage: 0
      };
    }

    try {
      let used = 0;
      for (const key in localStorage) {
        if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
          used += localStorage[key].length + key.length;
        }
      }

      // Estimate quota (most browsers have ~5-10MB)
      const quota = 5 * 1024 * 1024; // 5MB estimate
      const usage = (used / quota) * 100;

      return {
        available: true,
        used,
        quota,
        usage: Math.round(usage * 100) / 100
      };
    } catch (error) {
      return {
        available: true,
        used: 0,
        quota: 0,
        usage: 0
      };
    }
  }

  private saveItemsToStorage(items: CartItem[], options: PersistenceOptions): void {
    if (isCartHydrationBlocked()) {
      this.clearCartFromLocalStorage();
      return;
    }

    try {
      this.stats.saveCount++;
      this.stats.lastSave = Date.now();

      let processedItems = items;

      // Validate items if requested
      if (options.validateBeforeSave) {
        const validationResult = this.validationService.validateCartItems(processedItems);
        if (!validationResult.isValid) {
          console.warn('Cart validation failed before save:', validationResult.errors);
        }
      }

      // Sanitize items if requested
      if (options.sanitizeBeforeSave) {
        processedItems = this.validationService.sanitizeCartItems(processedItems);
      }

      // Create backup before saving
      this.createBackup(processedItems);

      // Save to main storage
      const dataToSave = options.compressData 
        ? this.compressData(processedItems)
        : JSON.stringify(processedItems);
      
      localStorage.setItem(this.CART_STORAGE_KEY, dataToSave);

      // Save metadata
      this.saveMetadata(processedItems);

    } catch (error) {
      this.stats.errorCount++;
      this.stats.lastError = Date.now();
      this.errorHandler.handlePersistenceError(error, 'Save Cart to LocalStorage');
    }
  }

  private parseStoredData(data: string, options: PersistenceOptions): CartItem[] {
    try {
      let items: CartItem[];
      
      // Try to decompress if needed
      if (this.isCompressedData(data)) {
        items = this.decompressData(data);
      } else {
        items = JSON.parse(data);
      }

      // Validate loaded items if requested
      if (options.validateBeforeSave) {
        const validationResult = this.validationService.validateCartItems(items);
        if (!validationResult.isValid) {
          console.warn('Loaded cart validation failed:', validationResult.errors);
        }
      }

      // Sanitize loaded items if requested
      if (options.sanitizeBeforeSave) {
        items = this.validationService.sanitizeCartItems(items);
      }

      return items;
    } catch (error) {
      this.errorHandler.handlePersistenceError(error, 'Parse Stored Data');
      return [];
    }
  }

  private saveMetadata(items: CartItem[]): void {
    try {
      const metadata = {
        version: '1.0',
        lastModified: Date.now(),
        itemCount: items.length,
        totalQuantity: items.reduce((total, item) => total + item.quantity, 0)
      };
      
      localStorage.setItem(this.CART_METADATA_KEY, JSON.stringify(metadata));
    } catch (error) {
      console.warn('Failed to save cart metadata:', error);
    }
  }

  private compressData(items: CartItem[]): string {
    return JSON.stringify(items).replace(/\s+/g, '');
  }

  private decompressData(data: string): CartItem[] {
    return JSON.parse(data);
  }

  private isCompressedData(data: string): boolean {
    return !data.includes(' ');
  }
}
